// apps/web/src/features/russia-requests/components/editor/useRussiaEditorAutosave.ts
//
// Debounced autosave hook for the russia-rates manual editor.
//
// @aiPurpose Persist the in-progress draft after a 1500 ms pause without
//   stealing focus or interrupting input. First successful save promotes
//   the host into "persisted" mode and subsequent saves issue PATCH
//   instead of POST.
// @aiInvariant Never saves an invalid payload. The hook gates on a
//   `safeParse` against the wire schema in addition to RHF's own
//   validation lifecycle — an invalid payload would otherwise reach the
//   network and 400 the user mid-typing.
// @aiInvariant The host invokes `notifyChanged()` from each field's
//   `onChange` wrapper. We intentionally do NOT subscribe to
//   `form.watch` — that path interferes with userEvent typing across
//   tests (silent char loss after a prior test submitted + navigated).
// @aiInvariant Edits made while a save is in flight are coalesced into
//   `pendingSnapshotRef` and re-checked when the current save settles,
//   so the user never loses typing during a slow network round-trip.
//   Edits that flip the payload back to invalid mid-debounce cancel
//   the pending timer so a stale (previously valid) snapshot does not
//   reach the network.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import {
  CreateRussiaRequestManualInputSchema,
  type CreateRussiaRequestManualInput,
  type RussiaRequest,
} from '@magimp/shared';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface AutosaveState {
  status: AutosaveStatus;
  savedAt: Date | null;
  error: string | null;
}

export interface AutosaveHandle {
  state: AutosaveState;
  /** Call after every form-field change to (re)arm the debounced save. */
  notifyChanged: () => void;
  /**
   * Cancel a debounced timer that has not yet fired AND drop any
   * coalesced pending edit. Called by the explicit Save path before
   * its POST, so a stray autosave create cannot race the explicit
   * create and double-POST the non-idempotent manual endpoint
   *  (Codex RUS-941-003).
   */
  cancelPending: () => void;
}

export interface UseRussiaEditorAutosaveOptions {
  /** RHF form handle for the manual-create payload. */
  form: UseFormReturn<CreateRussiaRequestManualInput>;
  /** Already-persisted row id, or `null` before the first autosave. */
  persistedId: string | null;
  /** POST /russia-requests/manual. */
  create: (input: CreateRussiaRequestManualInput) => Promise<RussiaRequest>;
  /** PATCH /russia-requests/:id. */
  patch: (id: string, input: CreateRussiaRequestManualInput) => Promise<RussiaRequest>;
  /** Called once after a successful create with the new row id. */
  onPersisted: (id: string) => void;
  /** Called when a save error fires. Hosts surface this as a non-blocking toast. */
  onError?: (message: string) => void;
  /** Override the 1500 ms debounce; tests pass a shorter value. */
  debounceMs?: number;
}

const DEFAULT_DEBOUNCE_MS = 1500;

export function useRussiaEditorAutosave(opts: UseRussiaEditorAutosaveOptions): AutosaveHandle {
  const {
    form,
    persistedId,
    create,
    patch,
    onPersisted,
    onError,
    debounceMs = DEFAULT_DEBOUNCE_MS,
  } = opts;

  const [state, setState] = useState<AutosaveState>({
    status: 'idle',
    savedAt: null,
    error: null,
  });

  const lastSavedSnapshotRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);

  const persistedIdRef = useRef(persistedId);
  persistedIdRef.current = persistedId;
  const createRef = useRef(create);
  createRef.current = create;
  const patchRef = useRef(patch);
  patchRef.current = patch;
  const onPersistedRef = useRef(onPersisted);
  onPersistedRef.current = onPersisted;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const formRef = useRef(form);
  formRef.current = form;
  const debounceMsRef = useRef(debounceMs);
  debounceMsRef.current = debounceMs;

  // WHY: cancel any pending debounced save when the editor unmounts.
  //   Without this, a 1500 ms timer scheduled during typing in one test
  //   fires during the next test's setup, taking userEvent off course
  //   (silent character drop) and surfacing an unhandled MSW request
  //   error. Production also benefits — no spurious save after
  //   navigating away.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const pendingSnapshotRef = useRef<{ data: CreateRussiaRequestManualInput; snap: string } | null>(
    null,
  );

  const notifyChanged = useCallback(() => {
    const f = formRef.current;
    // WHY: parse first so an edit that flips the payload to invalid
    //   cancels a previously scheduled valid timer; otherwise the
    //   stale valid snapshot would still reach the network.
    const parsed = f.formState.isDirty
      ? CreateRussiaRequestManualInputSchema.safeParse(f.getValues())
      : null;

    if (!parsed || !parsed.success) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      // Codex RUS-941-002b: drop a pending coalesced edit too — we
      //   should not save an invalid form once the current in-flight
      //   save resolves.
      pendingSnapshotRef.current = null;
      return;
    }

    const snapshot = JSON.stringify(parsed.data);
    if (snapshot === lastSavedSnapshotRef.current) {
      // No diff vs what is already persisted — drop any stale pending
      //   schedule so we don't re-POST/PATCH the same payload.
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pendingSnapshotRef.current = null;
      return;
    }

    if (inFlightRef.current) {
      // Codex RUS-941-002a: stash the latest valid snapshot so we can
      //   re-run after the in-flight save settles. Edits during a slow
      //   network round-trip must not be lost.
      pendingSnapshotRef.current = { data: parsed.data, snap: snapshot };
      return;
    }

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void runSave(parsed.data, snapshot);
    }, debounceMsRef.current);
  }, []);

  async function runSave(input: CreateRussiaRequestManualInput, snap: string): Promise<void> {
    inFlightRef.current = true;
    setState({ status: 'saving', savedAt: null, error: null });
    try {
      const id = persistedIdRef.current;
      if (id === null) {
        const row = await createRef.current(input);
        lastSavedSnapshotRef.current = snap;
        onPersistedRef.current(row.id);
      } else {
        await patchRef.current(id, input);
        lastSavedSnapshotRef.current = snap;
      }
      setState({ status: 'saved', savedAt: new Date(), error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState({ status: 'error', savedAt: null, error: message });
      onErrorRef.current?.(message);
    } finally {
      inFlightRef.current = false;
      // Drain any edit that arrived while we were saving.
      const pending = pendingSnapshotRef.current;
      pendingSnapshotRef.current = null;
      if (pending && pending.snap !== lastSavedSnapshotRef.current) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          void runSave(pending.data, pending.snap);
        }, debounceMsRef.current);
      }
    }
  }

  const cancelPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingSnapshotRef.current = null;
  }, []);

  return { state, notifyChanged, cancelPending };
}
